import React, { useEffect, useState } from 'react';
import { profileData } from '../data/mockData';

/**
 * Cinematic "biometric ID confirmed" banner triggered by `whoami`.
 * Big logo + scanning lines + animated identity fields.
 */
const WhoamiBanner = () => {
  const [scanProgress, setScanProgress] = useState(0);
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const elapsed = now - start;
      const pct = Math.min(100, (elapsed / 1800) * 100);
      setScanProgress(pct);
      if (pct >= 100) {
        setConfirmed(true);
        return;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  const fields = [
    { label: 'ALIAS',     value: profileData.name,     delay: 0.2 },
    { label: 'CALLSIGN',  value: profileData.alias || 'Anonymous', delay: 0.45 },
    { label: 'ROLE',      value: profileData.title,    delay: 0.7 },
    { label: 'LOCATION',  value: profileData.location, delay: 0.95 },
    { label: 'CLEARANCE', value: 'L4 :: AUTHORIZED',   delay: 1.2 }
  ];

  return (
    <div className="whoami-banner" data-testid="whoami-banner">
      <div className="whoami-grid" aria-hidden="true" />

      <div className="whoami-portrait">
        <span className="whoami-corner whoami-corner-tl" />
        <span className="whoami-corner whoami-corner-tr" />
        <span className="whoami-corner whoami-corner-bl" />
        <span className="whoami-corner whoami-corner-br" />

        <div className="whoami-logo-stack">
          <img
            src={profileData.logo}
            alt={`${profileData.name} biometric`}
            className="whoami-logo"
            data-testid="whoami-logo"
            draggable={false}
          />
          <img
            src={profileData.logo}
            alt=""
            aria-hidden="true"
            className="whoami-logo whoami-logo-glitch whoami-logo-glitch-r"
            draggable={false}
          />
          <img
            src={profileData.logo}
            alt=""
            aria-hidden="true"
            className="whoami-logo whoami-logo-glitch whoami-logo-glitch-c"
            draggable={false}
          />
          <span className="whoami-corrupt-scan" aria-hidden="true" />
          <span className="whoami-corrupt-beam" aria-hidden="true" />
          <span className="whoami-corrupt-noise" aria-hidden="true" />
        </div>

        {!confirmed && (
          <div
            className="whoami-scanline"
            style={{ top: `${scanProgress}%` }}
            aria-hidden="true"
          />
        )}

        {confirmed && (
          <div className="whoami-stamp" data-testid="whoami-stamp">
            <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
              <path
                d="M5 12.5l4 4 10-10"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span>IDENTITY VERIFIED</span>
          </div>
        )}
      </div>

      <div className="whoami-info">
        <div className="whoami-status">
          {confirmed ? (
            <>
              <span className="whoami-dot whoami-dot-ok" />
              ACCESS GRANTED
            </>
          ) : (
            <>
              <span className="whoami-dot whoami-dot-scan" />
              SCANNING BIOMETRICS · {Math.floor(scanProgress)}%
            </>
          )}
        </div>

        <div className="whoami-fields">
          {fields.map((f) => (
            <div
              key={f.label}
              className="whoami-field"
              style={{ animationDelay: `${f.delay}s` }}
            >
              <span className="whoami-field-label">{f.label}</span>
              <span className="whoami-field-sep">::</span>
              <span className="whoami-field-value">{f.value}</span>
            </div>
          ))}
        </div>

        <div className="whoami-hash">
          <span className="whoami-hash-label">SIGNATURE_SHA</span>
          <span className="whoami-hash-value">
            0x4c{profileData.name.length}f{Date.now().toString(16).slice(-6)}…
          </span>
        </div>
      </div>
    </div>
  );
};

export default WhoamiBanner;
